import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  ArrowRightLeft,
  Boxes,
  Calendar,
  Clock,
  Layers,
  MapPin,
  Package,
  Printer,
  User,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { usePallet, usePalletMovements } from "~/hooks/wms";
import {
  MOVEMENT_TYPE_LABELS,
  PALLET_STATUS_LABELS,
  PALLET_TYPE_LABELS,
} from "~/types/wms";
import type { PalletStatus } from "~/types/wms";

const statusConfig: Record<
  PalletStatus,
  {
    variant: "default" | "secondary" | "destructive" | "outline";
    bgColor: string;
  }
> = {
  created: { variant: "secondary", bgColor: "bg-gray-100" },
  ready_for_warehouse: { variant: "default", bgColor: "bg-blue-100" },
  stored: { variant: "default", bgColor: "bg-green-100" },
  partial: { variant: "default", bgColor: "bg-yellow-100" },
  picked_full: { variant: "outline", bgColor: "bg-purple-100" },
  empty: { variant: "secondary", bgColor: "bg-gray-50" },
};

export function PalletDetailPage() {
  const { palletId } = useParams<{ palletId: string }>();
  const navigate = useNavigate();

  const { data: pallet, isLoading } = usePallet(palletId);
  const { data: movements = [], isLoading: movementsLoading } =
    usePalletMovements(palletId);

  const handleBack = () => {
    navigate("/wms/pallets");
  };

  const handleRelocate = () => {
    navigate("/wms/pallet-relocation", { state: { palletId } });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </PageContainer>
    );
  }

  if (!pallet) {
    return (
      <PageContainer>
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="py-12 text-center text-muted-foreground">
          <Package className="mx-auto h-12 w-12 opacity-30" />
          <p className="mt-2">Pallet no encontrado</p>
        </div>
      </PageContainer>
    );
  }

  const config = statusConfig[pallet.status];
  const position = pallet.position as any;
  const expirationDate = pallet.earliest_expiration || pallet.expiration_date;

  return (
    <PageContainer>
      {/* Header */}
      <header className="space-y-3">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-9 text-muted-foreground"
          onClick={handleBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-mono text-2xl font-bold">{pallet.code}</h1>
              <Badge variant={config.variant}>
                {PALLET_STATUS_LABELS[pallet.status]}
              </Badge>
              {pallet.pallet_type === "mixed" && (
                <Badge
                  variant="default"
                  className="bg-purple-100 text-purple-700 hover:bg-purple-200"
                >
                  {PALLET_TYPE_LABELS[pallet.pallet_type]}
                </Badge>
              )}
            </div>
            {pallet.barcode && (
              <p className="font-mono text-sm text-muted-foreground">
                {pallet.barcode}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="mr-2 h-4 w-4" />
              Imprimir Etiqueta
            </Button>
            <Button onClick={handleRelocate}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              Reubicar
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Position Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Ubicación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {position ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-4 text-center">
                  <p className="font-mono text-2xl font-bold">
                    {position.full_code || position.code}
                  </p>
                  {position.barcode && (
                    <p className="text-sm text-muted-foreground">
                      {position.barcode}
                    </p>
                  )}
                </div>
                {position.rack && (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rack:</span>
                      <span className="font-medium">
                        {position.rack.full_code}
                      </span>
                    </div>
                    {position.rack.hallway && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Pasillo:</span>
                        <span className="font-medium">
                          {position.rack.hallway.full_code}
                        </span>
                      </div>
                    )}
                    {position.rack.hallway?.warehouse && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Bodega:</span>
                        <span className="font-medium">
                          {position.rack.hallway.warehouse.name}
                        </span>
                      </div>
                    )}
                    {position.level && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nivel:</span>
                        <span className="font-medium">N{position.level}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                <MapPin className="mx-auto h-8 w-8 opacity-30" />
                <p className="mt-2">Sin ubicación asignada</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inventory Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Boxes className="h-4 w-4" />
              Inventario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-3xl font-bold">
                    {pallet.current_box_count}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Cajas Actuales
                  </p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-3xl font-bold">
                    {pallet.boxes_per_pallet}
                  </p>
                  <p className="text-xs text-muted-foreground">Capacidad</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {pallet.initial_box_count !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Cajas Iniciales:
                    </span>
                    <span className="font-medium">
                      {pallet.initial_box_count}
                    </span>
                  </div>
                )}
                {pallet.weight_kg && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Peso:</span>
                    <span className="font-medium">{pallet.weight_kg} kg</span>
                  </div>
                )}
              </div>
              {expirationDate && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Vencimiento:
                      </span>
                    </div>
                    <span
                      className={`font-medium ${
                        dayjs(expirationDate).isBefore(dayjs().add(30, "day"))
                          ? "text-red-600"
                          : ""
                      }`}
                    >
                      {dayjs(expirationDate).format("DD/MM/YYYY")}
                    </span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Timestamps Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4" />
              Historial
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {pallet.exited_production_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Salió Producción:
                  </span>
                  <span>
                    {dayjs(pallet.exited_production_at).format("DD/MM HH:mm")}
                  </span>
                </div>
              )}
              {pallet.received_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Recibido:</span>
                  <span>{dayjs(pallet.received_at).format("DD/MM HH:mm")}</span>
                </div>
              )}
              {pallet.last_picked_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Último Picking:</span>
                  <span>
                    {dayjs(pallet.last_picked_at).format("DD/MM HH:mm")}
                  </span>
                </div>
              )}
              {pallet.shipped_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Despachado:</span>
                  <span>{dayjs(pallet.shipped_at).format("DD/MM HH:mm")}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Creado:</span>
                <span>
                  {dayjs(pallet.created_at).format("DD/MM/YYYY HH:mm")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Actualizado:</span>
                <span>
                  {dayjs(pallet.updated_at).format("DD/MM/YYYY HH:mm")}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batches Table (for mixed pallets or any pallet with batches) */}
      {pallet.batches && pallet.batches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4" />
              Lotes en Pallet ({pallet.batches.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead className="text-right">Cajas</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead>Agregado</TableHead>
                  <TableHead>Notas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pallet.batches.map((batch, index) => (
                  <TableRow
                    key={batch.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">
                      {batch.product?.short_name || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-mono text-sm">
                          {batch.production_batch?.batch_number || "-"}
                        </span>
                        {batch.lot_number && (
                          <span className="text-xs text-muted-foreground">
                            Lote: {batch.lot_number}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {batch.box_count}
                    </TableCell>
                    <TableCell>
                      {batch.expiration_date ? (
                        <span
                          className={
                            dayjs(batch.expiration_date).isBefore(
                              dayjs().add(30, "day")
                            )
                              ? "font-medium text-red-600"
                              : ""
                          }
                        >
                          {dayjs(batch.expiration_date).format("DD/MM/YYYY")}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {dayjs(batch.added_at).format("DD/MM HH:mm")}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {batch.notes || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Movement History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ArrowRightLeft className="h-4 w-4" />
            Movimientos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {movementsLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : movements.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <ArrowRightLeft className="mx-auto h-8 w-8 opacity-30" />
              <p className="mt-2">No hay movimientos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Desde</TableHead>
                  <TableHead>Hacia</TableHead>
                  <TableHead className="text-right">Cajas</TableHead>
                  <TableHead>Realizado por</TableHead>
                  <TableHead>Motivo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement, index) => (
                  <TableRow
                    key={movement.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      {dayjs(movement.performed_at).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {MOVEMENT_TYPE_LABELS[
                          movement.movement_type as keyof typeof MOVEMENT_TYPE_LABELS
                        ] || movement.movement_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {movement.from_position ? (
                        <span className="font-mono text-sm">
                          {movement.from_position.full_code ||
                            movement.from_position.code}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {movement.to_position ? (
                        <span className="font-mono text-sm">
                          {movement.to_position.full_code ||
                            movement.to_position.code}
                        </span>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {movement.box_quantity}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-muted-foreground" />
                        {movement.performer
                          ? `${movement.performer.name || ""} ${movement.performer.lastname || ""}`.trim() ||
                            "-"
                          : "-"}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {movement.reason || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default PalletDetailPage;
